import { Fragment, ReactNode, isValidElement, Children, cloneElement } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { FileText } from "lucide-react";
import { cn } from "@/lib/utils";

export interface MentionProfileLite {
  id: string;
  full_name?: string | null;
  email?: string | null;
}

export interface LeadRefLite {
  id: string;
  title: string;
}

interface MessageMarkdownProps {
  message: string;
  profiles?: MentionProfileLite[];
  leads?: LeadRefLite[];
  onLeadClick?: (leadId: string) => void;
  className?: string;
}

// Placeholder pattern that survives markdown parsing: zero-width markers around an id.
const LEAD_PLACEHOLDER_PREFIX = "\u2063LEAD\u2063";
const LEAD_PLACEHOLDER_SUFFIX = "\u2063/LEAD\u2063";

/**
 * Pre-process a raw message: convert internal lead-reference syntax into
 * inline placeholder tokens that markdown will leave untouched, so we can
 * later swap them for interactive Badge components in the rendered output.
 */
function preprocessMessage(raw: string): { text: string; leadMap: Record<string, LeadRefLite> } {
  const leadMap: Record<string, LeadRefLite> = {};
  // New format: [Lead: <uuid>|<title>]
  let text = raw.replace(/\[Lead:\s*([a-f0-9-]+)\|([^\]]+)\]/g, (_, id, title) => {
    leadMap[id] = { id, title };
    return `${LEAD_PLACEHOLDER_PREFIX}${id}${LEAD_PLACEHOLDER_SUFFIX}`;
  });
  return { text, leadMap };
}

function renderTextSegment(
  text: string,
  ctx: { profiles: MentionProfileLite[]; leadMap: Record<string, LeadRefLite>; leads: LeadRefLite[]; onLeadClick?: (id: string) => void },
  keyBase: string
): ReactNode[] {
  // Split on lead placeholders and @mentions in one pass.
  const combined = new RegExp(
    `(${LEAD_PLACEHOLDER_PREFIX}[a-f0-9-]+${LEAD_PLACEHOLDER_SUFFIX})|(@[^\\s@]+(?:\\s[^\\s@]+)?)`,
    "g"
  );
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = combined.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const leadMatch = match[1];
    const mentionMatch = match[2];
    if (leadMatch) {
      const id = leadMatch.slice(LEAD_PLACEHOLDER_PREFIX.length, -LEAD_PLACEHOLDER_SUFFIX.length);
      const lead = ctx.leadMap[id];
      if (lead) {
        nodes.push(
          <Tooltip key={`${keyBase}-lead-${i}`}>
            <TooltipTrigger asChild>
              <Badge
                variant="secondary"
                className={cn(
                  "mx-1 inline-flex items-center align-baseline",
                  ctx.onLeadClick && "cursor-pointer hover:bg-primary/20 transition-colors"
                )}
                onClick={ctx.onLeadClick ? () => ctx.onLeadClick!(lead.id) : undefined}
              >
                <FileText className="h-3 w-3 mr-1" />
                {lead.title}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>Click to view lead</p>
            </TooltipContent>
          </Tooltip>
        );
      } else {
        nodes.push(leadMatch);
      }
    } else if (mentionMatch) {
      const name = mentionMatch.slice(1);
      const matchedProfile = ctx.profiles.find(
        (p) => p.full_name === name || p.email === name
      );
      if (matchedProfile) {
        nodes.push(
          <span
            key={`${keyBase}-mention-${i}`}
            className="bg-primary/20 text-primary px-1 rounded font-medium"
          >
            {mentionMatch}
          </span>
        );
      } else {
        nodes.push(mentionMatch);
      }
    }
    lastIndex = match.index + match[0].length;
    i++;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

function processChildren(
  children: ReactNode,
  ctx: { profiles: MentionProfileLite[]; leadMap: Record<string, LeadRefLite>; leads: LeadRefLite[]; onLeadClick?: (id: string) => void },
  keyBase = "n"
): ReactNode {
  const arr = Children.toArray(children);
  return arr.map((child, idx) => {
    const key = `${keyBase}-${idx}`;
    if (typeof child === "string") {
      const segs = renderTextSegment(child, ctx, key);
      return <Fragment key={key}>{segs}</Fragment>;
    }
    if (isValidElement(child)) {
      const el = child as React.ReactElement<{ children?: ReactNode }>;
      if (el.props && el.props.children) {
        return cloneElement(el, { key }, processChildren(el.props.children, ctx, key));
      }
      return cloneElement(el, { key });
    }
    return child;
  });
}

export const MessageMarkdown = ({
  message,
  profiles = [],
  leads = [],
  onLeadClick,
  className,
}: MessageMarkdownProps) => {
  // Merge passed-in leads as fallbacks into placeholder map
  const { text, leadMap } = preprocessMessage(message);
  const mergedMap: Record<string, LeadRefLite> = { ...leadMap };
  for (const l of leads) if (!mergedMap[l.id]) mergedMap[l.id] = l;

  const ctx = { profiles, leadMap: mergedMap, leads, onLeadClick };

  const wrap = (Tag: keyof JSX.IntrinsicElements, extra = "") =>
    ({ children }: { children?: ReactNode }) => (
      // @ts-ignore — generic intrinsic
      <Tag className={extra}>{processChildren(children, ctx)}</Tag>
    );

  return (
    <div className={cn("message-md text-sm break-words [overflow-wrap:anywhere]", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: wrap("p", "mb-1 last:mb-0 whitespace-pre-wrap"),
          strong: wrap("strong", "font-semibold"),
          em: wrap("em", "italic"),
          del: wrap("del", "line-through"),
          ul: wrap("ul", "list-disc pl-5 my-1 space-y-0.5"),
          ol: wrap("ol", "list-decimal pl-5 my-1 space-y-0.5"),
          li: wrap("li", ""),
          blockquote: wrap(
            "blockquote",
            "border-l-2 border-muted-foreground/40 pl-3 italic text-muted-foreground my-1"
          ),
          h1: wrap("h1", "text-base font-semibold mt-2 mb-1"),
          h2: wrap("h2", "text-sm font-semibold mt-2 mb-1"),
          h3: wrap("h3", "text-sm font-semibold mt-1 mb-1"),
          code: ({ children, className: cls, ...rest }: any) => {
            const isBlock = /language-/.test(cls || "");
            if (isBlock) {
              return (
                <code className="block bg-muted rounded px-2 py-1 text-xs font-mono whitespace-pre-wrap overflow-x-auto" {...rest}>
                  {children}
                </code>
              );
            }
            return (
              <code className="bg-muted rounded px-1 py-0.5 text-xs font-mono" {...rest}>
                {children}
              </code>
            );
          },
          pre: ({ children }: any) => (
            <pre className="bg-muted rounded p-2 my-1 text-xs font-mono whitespace-pre-wrap overflow-x-auto">
              {children}
            </pre>
          ),
          a: ({ children, href }: any) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2 hover:opacity-80"
            >
              {processChildren(children, ctx)}
            </a>
          ),
          hr: () => <hr className="my-2 border-border" />,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
};